/**
 * Browser error reporting, into GlitchTip.
 *
 * WHY THIS IS HAND-ROLLED. `@sentry/react` is about 30 kB gzipped against a
 * 65 kB bundle — a 45% increase in what a customer downloads before they can
 * see whether their server is up, on the screen they open during an incident,
 * on a phone. What we actually need is "tell us when the panel breaks", and
 * that is a POST. The SDK's tracing, replay, breadcrumb buffer and integration
 * machinery are all things this panel does not use.
 *
 * WHAT IT REPORTS: uncaught exceptions and unhandled promise rejections. That
 * is the whole surface. Handled errors already become a visible message to the
 * customer and a server-side log line; sending those too would bury the ones
 * nobody saw.
 *
 * WHAT IT NEVER SENDS: no request bodies, no headers, no cookies, no
 * localStorage, no URL query string — a console ticket travels in one — and no
 * user identity beyond the fact that a session existed. The panel's URLs carry
 * instance ids, which are meaningless outside our own database, so paths are
 * kept.
 */

interface Dsn {
  endpoint: string;
  key: string;
}

/**
 * `https://KEY@host/PROJECT` → the store endpoint.
 *
 * Parsed rather than configured as two values, so the DSN can be pasted from
 * GlitchTip exactly as it is shown there.
 */
function parseDsn(raw: string): Dsn | null {
  try {
    const url = new URL(raw);
    const project = url.pathname.replace(/^\/+/, '');
    if (!url.username || project === '') return null;
    return {
      endpoint: `${url.protocol}//${url.host}/api/${project}/store/`,
      key: url.username,
    };
  } catch {
    return null;
  }
}

/**
 * At most this many events per page load.
 *
 * A render loop that throws on every frame would otherwise send thousands of
 * identical events and make the project unusable — and the first one already
 * said everything the tenth would.
 */
const MAX_EVENTS = 5;

let sent = 0;
let dsn: Dsn | null = null;
let release: string | undefined;

/** Strip anything that could identify a person or carry a secret. */
function safeUrl(href: string): string {
  try {
    const u = new URL(href);
    return `${u.origin}${u.pathname}`;
  } catch {
    return '/';
  }
}

function report(kind: string, message: string, stack?: string): void {
  if (!dsn || sent >= MAX_EVENTS) return;
  sent += 1;

  const body = JSON.stringify({
    platform: 'javascript',
    logger: 'panel',
    level: 'error',
    release,
    environment: import.meta.env.MODE,
    timestamp: new Date().toISOString(),
    exception: { values: [{ type: kind, value: message.slice(0, 500), stacktrace: undefined }] },
    // The stack as plain text: parsing it into frames is what the SDK's bulk
    // buys you, and an unparsed stack is still readable by a human.
    extra: { stack: stack?.slice(0, 4000) },
    request: { url: safeUrl(window.location.href) },
    tags: { service: 'vps-panel' },
  });

  // `keepalive` so a report survives the navigation that often follows a crash.
  // Failure is ignored on purpose: an error reporter that throws is worse than
  // one that misses an event.
  void fetch(`${dsn.endpoint}?sentry_key=${encodeURIComponent(dsn.key)}&sentry_version=7`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body,
    keepalive: true,
    mode: 'cors',
  }).catch(() => undefined);
}

/**
 * Start listening. Safe to call when no DSN is configured — it simply does
 * nothing, which is the right behaviour on a developer's machine.
 */
export function startTelemetry(): void {
  // Defaulted in code rather than required from the build environment. A DSN is
  // a write-only ingest key that ships to every visitor in this bundle anyway,
  // so the only thing an environment variable adds here is a way to deploy a
  // panel that silently reports nothing. `VITE_GLITCHTIP_DSN` still overrides,
  // and setting it empty turns reporting off.
  const configured = import.meta.env.VITE_GLITCHTIP_DSN;
  const raw =
    configured === undefined
      ? 'https://855ed7634ab647d98651ceb4f162be3d@glitch.vgncloud.com/3'
      : configured;
  if (typeof raw !== 'string' || raw.trim() === '') return;

  dsn = parseDsn(raw.trim());
  if (!dsn) return;
  release = import.meta.env.VITE_RELEASE || undefined;

  window.addEventListener('error', (event) => {
    report(event.error?.name ?? 'Error', event.message ?? 'Unknown error', event.error?.stack);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason: unknown = event.reason;
    if (reason instanceof Error) {
      report(reason.name, reason.message, reason.stack);
      return;
    }
    report('UnhandledRejection', typeof reason === 'string' ? reason : 'Non-Error rejection');
  });
}
